<script setup>
  import { ref, onMounted, watch, nextTick } from 'vue';

  const props = defineProps({
    svgContent: {
      type: String,
      required: true
    },

    groupImages: {
      type: Array,
      default: () => []
    },

    // Largura do canvas
    width: {
      type: Number,
      default: null
    },

    // Altura do canvas
    height: {
      type: Number,
      default: null
    },

    // Qualidade (pixel ratio)
    pixelRatio: {
      type: Number,
      default: () => window.devicePixelRatio || 2
    },

    // Background color
    backgroundColor: {
      type: String,
      default: 'transparent'
    },

    // Classes CSS
    customClass: {
      type: String,
      default: ''
    },

    isSheet: {
      type: Boolean,
      default: false
    }


  });

  const canvasRef = ref(null);
  const isLoading = ref(true);
  const error = ref(null);
  const viewBox = ref({ x: 0, y: 0, width: 300, height: 300});
  const groups = ref([]);
  const looseShapes = ref([]);

  const parseSvg = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(props.svgContent, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');

    if (!svgElement) {
      throw new Error('SVG inválido');
    }

    // Extrai viewBox
    const vb = svgElement.getAttribute('viewBox');
    if (vb) {
      const parts = vb.split(' ').map(parseFloat);
      viewBox.value = {
        x: parts[0],
        y: parts[1],
        width: parts[2],
        height: parts[3]
      };
    } else {
      const w = parseFloat(svgElement.getAttribute('width') || 300);
      const h = parseFloat(svgElement.getAttribute('height') || 300);
      viewBox.value = { x: 0, y: 0, width: w, height: h };
    }


    // Grupos com id
    const groupElements = svgElement.querySelectorAll('g[id]');
    groups.value = [];

    groupElements.forEach(groupEl => {
      const groupId = groupEl.getAttribute('id');
      const groupTransform = groupEl.getAttribute('transform') || '';
      const shapes = [];

      groupEl.querySelectorAll('rect, polygon, path, circle, ellipse').forEach(shape => {
        const shapeData = {
          type: shape.tagName.toLowerCase(),
          attributes: {},
          // transforma do shape + herda do grupo
          transformOps: combineTransforms(groupTransform, shape.getAttribute('transform') || '')
        };
        // Copia atributos relevantes
        Array.from(shape.attributes).forEach(attr => {
          shapeData.attributes[attr.name] = attr.value;
        });
        shapes.push(shapeData);
      });

      groups.value.push({
        id: groupId,
        shapes: shapes
      });
    });

    // Formas soltas (fora de grupo)
    looseShapes.value = [];
    svgElement.querySelectorAll(':scope > path, :scope > rect, :scope > polygon, :scope > circle, :scope > ellipse')
      .forEach(shape => {
        const s = {
          type: shape.tagName.toLowerCase(),
          attributes: {},
          transformOps: parseTransformList(shape.getAttribute('transform') || '')
        };
        Array.from(shape.attributes).forEach(attr => {
          s.attributes[attr.name] = attr.value;
        });
        looseShapes.value.push(s);
      });
  };



  const loadImages = async () => {
    const imageMap = {};
    const promises = [];

    for (const groupImage of props.groupImages) {
      const { groupId, imageUrl, textureUrl } = groupImage;
      const urlsToLoad = [];

      if (imageUrl) urlsToLoad.push({ key: groupId + '_base', url: imageUrl });
      if (textureUrl) urlsToLoad.push({ key: groupId + '_texture', url: textureUrl });

      urlsToLoad.forEach(({ key, url }) => {
        promises.push(
          new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              imageMap[key] = img;
              resolve();
            };
            img.onerror = () => {
              imageMap[key] = null;
              console.warn(`Falha ao carregar imagem para ${key}: ${url}`);
              resolve();
            };
            img.src = url;
          })
        );
      });
    }

    await Promise.all(promises);
    return imageMap;
  };

  const parseTransformList = (str) => {
    if (!str || typeof str !== 'string') return [];
    const ops = [];
    const re = /(matrix|translate|rotate|scale|skewX|skewY)\s*\(([^)]+)\)/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      const type = m[1];
      const nums = m[2].split(/[\s,]+/).map(parseFloat).filter(n => !Number.isNaN(n));
      ops.push({ type, nums });
    }
    return ops;
  };

  const applyTransforms = (ctx, ops) => {
    if (!ops || ops.length === 0) return;
    for (const { type, nums } of ops) {
      switch (type) {
        case 'matrix': {
          if (nums.length === 6) ctx.transform(nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]);
          break;
        }
        case 'translate': {
          const tx = nums[0] || 0;
          const ty = (nums.length > 1 ? nums[1] : 0) || 0;
          ctx.translate(tx, ty);
          break;
        }
        case 'rotate': {
          const angle = (nums[0] || 0) * Math.PI / 180;
          if (nums.length >= 3) {
            const cx = nums[1] || 0;
            const cy = nums[2] || 0;
            ctx.translate(cx, cy);
            ctx.rotate(angle);
            ctx.translate(-cx, -cy);
          } else {
            ctx.rotate(angle);
          }
          break;
        }
        case 'scale': {
          const sx = (nums[0] ?? 1);
          const sy = (nums.length > 1 ? nums[1] : sx);
          ctx.scale(sx, sy);
          break;
        }
        case 'skewX': {
          const ax = (nums[0] || 0) * Math.PI / 180;
          ctx.transform(1, 0, Math.tan(ax), 1, 0, 0);
          break;
        }
        case 'skewY': {
          const ay = (nums[0] || 0) * Math.PI / 180;
          ctx.transform(1, Math.tan(ay), 0, 1, 0, 0);
          break;
        }
      }
    }
  };

  const combineTransforms = (groupTransformStr, shapeTransformStr) => {
    const res = [];
    const g = parseTransformList(groupTransformStr);
    const s = parseTransformList(shapeTransformStr);
    if (g.length) res.push(...g);
    if (s.length) res.push(...s);
    return res;
  };


  const buildPathFromShape = (shape) => {
    const { type, attributes } = shape;
    let p = null;

    if (type === 'path' && attributes.d) p = new Path2D(attributes.d);
    else if (type === 'rect') {
      const { x, y, width: w, height: h } = attributes;
      p = new Path2D();
      p.rect(parseFloat(x || 0), parseFloat(y || 0), parseFloat(w || 0), parseFloat(h || 0));
    }
    else if (type === 'polygon' && attributes.points) {
      const pts = attributes.points.trim().split(/[\s,]+/).map(parseFloat);
      if (pts.length >= 4) {
        p = new Path2D();
        for (let i = 0; i < pts.length; i += 2) {
          if (i === 0) p.moveTo(pts[i], pts[i + 1]);
          else p.lineTo(pts[i], pts[i + 1]);
        }
        p.closePath();
      }
    }
    else if (type === 'circle') {
      const { cx, cy, r } = attributes;
      p = new Path2D();
      p.arc(parseFloat(cx || 0), parseFloat(cy || 0), parseFloat(r || 0), 0, Math.PI * 2);
    }
    else if (type === 'ellipse') {
      const { cx, cy, rx, ry } = attributes;
      p = new Path2D();
      p.ellipse(parseFloat(cx || 0), parseFloat(cy || 0), parseFloat(rx || 0), parseFloat(ry || 0), 0, 0, Math.PI * 2);
    }

    return p;
  };

  const computeCoverDraw = (img, targetW, targetH) => {
    const scale = Math.max(targetW / img.width, targetH / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = (targetW - dw) / 2;
    const dy = (targetH - dh) / 2;
    return { dx, dy, dw, dh };
  };


  const parseDashArray = (val) => {
    if (!val) return [];
    return val.split(/[\s,]+/).map(n => parseFloat(n)).filter(n => !Number.isNaN(n));
  };


  const applyStrokeIfNeeded = (ctx, shape, path) => {
    const a = shape.attributes || {};
    const stroke = a.stroke;
    if (!stroke || stroke === 'none') return;

    const strokeWidth = parseFloat(a['stroke-width'] || 1);

    ctx.save();
    if (shape.transformOps?.length) applyTransforms(ctx, shape.transformOps);

    ctx.strokeStyle = stroke;
    ctx.lineWidth = Number.isFinite(strokeWidth) ? strokeWidth : 1;

    const lineJoin = a['stroke-linejoin'];
    if (lineJoin) ctx.lineJoin = lineJoin;
    const lineCap = a['stroke-linecap'];
    if (lineCap) ctx.lineCap = lineCap;
    const miterLimit = parseFloat(a['stroke-miterlimit'] || '');
    if (Number.isFinite(miterLimit)) ctx.miterLimit = miterLimit;
    const strokeOpacity = parseFloat(a['stroke-opacity'] || '');
    if (Number.isFinite(strokeOpacity)) ctx.globalAlpha = strokeOpacity;

    const dash = parseDashArray(a['stroke-dasharray']);
    if (dash.length > 0 && ctx.setLineDash) {
      ctx.setLineDash(dash);
    }
    const dashOffset = parseFloat(a['stroke-dashoffset'] || '');
    if (Number.isFinite(dashOffset)) {
      try { ctx.lineDashOffset = dashOffset; } catch (_) {}
    }

    ctx.stroke(path);
    ctx.restore();
  };

  const renderCanvas = async () => {
    const canvas = canvasRef.value;
    if (!canvas || !canvas.getContext) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      parseSvg();
      const imageMap = await loadImages();

      const ctx = canvas.getContext('2d', { alpha: true });

      const canvasWidth = props.width || viewBox.value.width;
      const canvasHeight = props.height || viewBox.value.height;

      canvas.width = canvasWidth * props.pixelRatio;
      canvas.height = canvasHeight * props.pixelRatio;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(props.pixelRatio, props.pixelRatio);

      // Background color
      if (props.backgroundColor !== 'transparent') {
        ctx.fillStyle = props.backgroundColor;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }

      const scaleX = canvasWidth / viewBox.value.width;
      const scaleY = canvasHeight / viewBox.value.height;

      ctx.save();
      ctx.scale(scaleX, scaleY);

      // Função para calcular bounding box de um shape
      const getShapeBoundingBox = (shape) => {
        const attrs = shape.attributes;

        if (shape.type === 'rect') {
          return {
            x: parseFloat(attrs.x || 0),
            y: parseFloat(attrs.y || 0),
            width: parseFloat(attrs.width || 0),
            height: parseFloat(attrs.height || 0)
          };
        }

        if (shape.type === 'polygon' && attrs.points) {
          const pts = attrs.points.trim().split(/[\s,]+/).map(parseFloat);
          if (pts.length < 4) return null;

          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) {
            minX = Math.min(minX, pts[i]);
            maxX = Math.max(maxX, pts[i]);
            minY = Math.min(minY, pts[i + 1]);
            maxY = Math.max(maxY, pts[i + 1]);
          }
          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
          };
        }

        if (shape.type === 'circle') {
          const cx = parseFloat(attrs.cx || 0);
          const cy = parseFloat(attrs.cy || 0);
          const r = parseFloat(attrs.r || 0);
          return {
            x: cx - r,
            y: cy - r,
            width: r * 2,
            height: r * 2
          };
        }

        if (shape.type === 'ellipse') {
          const cx = parseFloat(attrs.cx || 0);
          const cy = parseFloat(attrs.cy || 0);
          const rx = parseFloat(attrs.rx || 0);
          const ry = parseFloat(attrs.ry || 0);
          return {
            x: cx - rx,
            y: cy - ry,
            width: rx * 2,
            height: ry * 2
          };
        }

        // Para paths, usa o viewBox como fallback
        return {
          x: viewBox.value.x,
          y: viewBox.value.y,
          width: viewBox.value.width,
          height: viewBox.value.height
        };
      };

      // Função para calcular posição/dimensão para "cover" dentro de um bounding box
      const computeCoverForBounds = (img, boundsW, boundsH) => {
        const imgRatio = img.width / img.height;
        const boundsRatio = boundsW / boundsH;

        let drawW, drawH, drawX, drawY;

        if (imgRatio > boundsRatio) {
          drawH = boundsH;
          drawW = boundsH * imgRatio;
          drawX = (boundsW - drawW) / 2;
          drawY = 0;
        } else {
          drawW = boundsW;
          drawH = boundsW / imgRatio;
          drawX = 0;
          drawY = (boundsH - drawH) / 2;
        }

        return { drawX, drawY, drawW, drawH };
      };

      // Função auxiliar para obter a imagem de um grupo
      const getImageForGroup = (groupId) => {
        const groupProps = props.groupImages.find(g => g.groupId === groupId);
        if (!groupProps) return null;

        const imageKey = groupProps.textureUrl
          ? groupId + '_texture'
          : groupId + '_base';
        return imageMap[imageKey] || null;
      };

      // ========== MODO isSheet ==========
      // Quando isSheet=true, cada <rect> e <polygon> é preenchido com a imagem COMPLETA
      // do grupo correspondente (ajustada ao bounding box, estilo "cover")
      if (props.isSheet) {

        // Função auxiliar para desenhar shape (rect ou polygon) com a imagem completa (cover)
        const fillShapeWithFullImage = (shape, image) => {
          // Aplica a rect e polygon
          if (shape.type !== 'rect' && shape.type !== 'polygon') return false;

          const bounds = getShapeBoundingBox(shape);
          if (!bounds || bounds.width <= 0 || bounds.height <= 0) return false;

          const p = buildPathFromShape(shape);
          if (!p) return false;

          ctx.save();
          if (shape.transformOps?.length) applyTransforms(ctx, shape.transformOps);

          if (image) {
            // Clipa ao shape
            ctx.clip(p);

            // Calcula posição para "cover" dentro do bounding box deste shape
            const { drawX, drawY, drawW, drawH } = computeCoverForBounds(image, bounds.width, bounds.height);

            // Desenha a imagem completa ajustada ao bounding box
            ctx.drawImage(
              image,
              bounds.x + drawX,
              bounds.y + drawY,
              drawW,
              drawH
            );
          } else {
            // Fallback: usa fill original do SVG
            const fill = shape.attributes.fill;
            if (fill && fill !== 'none') {
              ctx.fillStyle = fill;
              ctx.fill(p);
            }
          }

          ctx.restore();
          applyStrokeIfNeeded(ctx, shape, p);
          return true;
        };

        // Função auxiliar para desenhar outros shapes (circle, ellipse, path) normalmente
        const drawOtherShape = (shape, image) => {
          const p = buildPathFromShape(shape);
          if (!p) return;

          ctx.save();
          if (shape.transformOps?.length) applyTransforms(ctx, shape.transformOps);

          if (image) {
            // Com imagem: usa pattern
            ctx.clip(p);
            const pattern = ctx.createPattern(image, 'repeat');
            if (pattern) {
              ctx.fillStyle = pattern;
              ctx.fillRect(viewBox.value.x, viewBox.value.y, viewBox.value.width, viewBox.value.height);
            }
          } else {
            // Sem imagem: usa fill original
            const fill = shape.attributes.fill;
            const fillOpacity = parseFloat(shape.attributes['fill-opacity'] || '');

            if (fill && fill !== 'none') {
              ctx.fillStyle = fill;
              if (Number.isFinite(fillOpacity)) ctx.globalAlpha = fillOpacity;
              ctx.fill(p);
            }
          }

          ctx.restore();
          applyStrokeIfNeeded(ctx, shape, p);
        };

        // Processa grupos - cada grupo usa a SUA imagem correspondente
        for (const group of groups.value) {
          const groupImage = getImageForGroup(group.id);

          for (const shape of group.shapes) {
            if (shape.type === 'rect' || shape.type === 'polygon') {
              fillShapeWithFullImage(shape, groupImage);
            } else {
              drawOtherShape(shape, groupImage);
            }
          }
        }

        // Processa formas soltas (usam a primeira imagem disponível como fallback)
        const fallbackImage = props.groupImages[0]
          ? imageMap[props.groupImages[0].textureUrl
            ? props.groupImages[0].groupId + '_texture'
            : props.groupImages[0].groupId + '_base']
          : null;

        for (const shape of looseShapes.value) {
          if (shape.type === 'rect' || shape.type === 'polygon') {
            fillShapeWithFullImage(shape, fallbackImage);
          } else {
            drawOtherShape(shape, fallbackImage);
          }
        }

      } else {
        // ========== MODO NORMAL (isSheet=false) ==========
        for (const group of groups.value) {
          const groupProps = props.groupImages.find(g => g.groupId === group.id);
          if (!groupProps) continue;

          const isTextured = groupProps.textureUrl;
          const imageKey = isTextured ? group.id + '_texture' : group.id + '_base';
          const image = imageMap[imageKey];
          const isColorFillOnly = !image;

          for (const shape of group.shapes) {
            const p = buildPathFromShape(shape);
            if (!p) continue;

            ctx.save();
            if (shape.transformOps?.length) applyTransforms(ctx, shape.transformOps);

            if (isColorFillOnly) {
              const fill = shape.attributes.fill;
              const fillOpacity = parseFloat(shape.attributes['fill-opacity'] || '');

              if (fill && fill !== 'none') {
                ctx.fillStyle = fill;
                if (Number.isFinite(fillOpacity)) ctx.globalAlpha = fillOpacity;
                ctx.fill(p);
              }
            } else {
              ctx.clip(p);

              const pattern = ctx.createPattern(image, 'repeat');
              if (pattern) {
                ctx.fillStyle = pattern;
                ctx.fillRect(viewBox.value.x, viewBox.value.y, viewBox.value.width, viewBox.value.height);
              } else {
                const { dx, dy, dw, dh } = computeCoverDraw(image, viewBox.value.width, viewBox.value.height);
                ctx.drawImage(image, dx + viewBox.value.x, dy + viewBox.value.y, dw, dh);
              }
            }

            ctx.restore();
            applyStrokeIfNeeded(ctx, shape, p);
          }
        }

        if (looseShapes.value.length) {
          for (const shape of looseShapes.value) {
            const p = buildPathFromShape(shape);
            if (!p) continue;

            const fill = shape.attributes.fill;
            const fillOpacity = parseFloat(shape.attributes['fill-opacity'] || '');

            if (fill && fill !== 'none') {
              ctx.save();
              if (shape.transformOps?.length) applyTransforms(ctx, shape.transformOps);
              ctx.fillStyle = fill;
              if (Number.isFinite(fillOpacity)) ctx.globalAlpha = fillOpacity;
              ctx.fill(p);
              ctx.restore();
            }

            applyStrokeIfNeeded(ctx, shape, p);
          }
        }
      }

      ctx.restore();

      isLoading.value = false;
    } catch (err) {
      error.value = err.message;
      isLoading.value = false;
      console.error('Canvas rendering error:', err);
    }
  };


  // Watchers
  watch(() => [props.svgContent, props.groupImages, props.width, props.height, props.pixelRatio, props.isSheet], () => {
    nextTick(renderCanvas);
  }, { deep: true });

  onMounted(() => {
    nextTick(renderCanvas);
  });


  defineExpose({
    render: renderCanvas
  });
</script>

<template>
  <div class="canvas-wrapper" :class="customClass">
    <canvas
      ref="canvasRef"
      :style="{
        width: `${width || viewBox.width}px`,
        height: `${height || viewBox.height}px`,
        display: 'block'
      }">
    </canvas>

    <div v-if="isLoading" class="loading-overlay">
      <span>Loading...</span>
    </div>

    <div v-if="error" class="error-overlay">
      <span>Error: {{ error }}</span>
    </div>
  </div>
</template>

<style scoped>
  .canvas-wrapper {
    position: relative;
    display: flex;
    align-items: center ;
    justify-content: center;



  }

  canvas {

    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }

  .loading-overlay,
  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.1);
    font-size: 12px;
    color: #666;
  }

  .error-overlay {
    background: rgba(255, 0, 0, 0.1);
    color: red;
  }
</style>
